import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ProfilePhoto } from '../ProfilePhoto';
import { XPBar } from '../common/XPBar';
import {
  Target, Award, Zap, RefreshCw, TrendingUp, ChevronDown, ChevronUp,
  CheckCircle2, Table2, LayoutList, Trophy, Star, Flame, Gem
} from 'lucide-react';

const ICONS = { trophy: Trophy, star: Star, target: Target, flame: Flame, gem: Gem };
const getIcon = (id) => ICONS[id] || Trophy;

function SkillStudentRow({ student, rank, color }) {
  return (
    <div className="flex items-center gap-2 py-1" data-testid={`skill-student-row-${student.student_id}`}>
      <span className="w-5 text-xs text-slate-400 font-semibold text-right flex-shrink-0">{rank}</span>
      <ProfilePhoto photoUrl={student.photo_url} name={student.student_name} size={26} />
      <span className="text-sm text-slate-700 truncate flex-1 min-w-0">{student.student_name}</span>
      {student.skill_level_name && (
        <Badge
          variant="outline"
          className="text-[10px] h-5 px-1.5 hidden sm:inline-flex flex-shrink-0"
          style={{ borderColor: color, color }}
        >
          Nv.{student.skill_level} {student.skill_level_name}
        </Badge>
      )}
      <div className="w-20 sm:w-32 flex-shrink-0">
        <XPBar pct={student.progress_pct ?? 0} color={color} />
      </div>
      <span className="text-xs font-bold w-24 text-right flex-shrink-0" style={{ color }}>
        {student.xp} XP{student.progress_pct != null ? ` · ${student.progress_pct}%` : ''}
      </span>
    </div>
  );
}

function AchievementStudentRow({ student, color }) {
  return (
    <div className="flex items-center gap-2 py-1" data-testid={`ach-student-row-${student.student_id}`}>
      <ProfilePhoto photoUrl={student.photo_url} name={student.student_name} size={26} />
      <span className="text-sm text-slate-700 truncate flex-1 min-w-0">{student.student_name}</span>
      {student.earned ? (
        <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 flex-shrink-0">
          <CheckCircle2 className="w-3.5 h-3.5" /> Obtenido
        </span>
      ) : (
        <>
          <div className="w-24 sm:w-36 flex-shrink-0">
            <XPBar pct={student.progress_pct} color={student.progress_pct > 0 ? color : '#94A3B8'} />
          </div>
          <span className="text-xs text-slate-500 w-10 text-right flex-shrink-0">{student.progress_pct}%</span>
        </>
      )}
    </div>
  );
}

function ExpandToggle({ isOpen, total, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
      data-testid={testId}
    >
      {isOpen ? (
        <><ChevronUp className="w-3.5 h-3.5" /> Ver menos</>
      ) : (
        <><ChevronDown className="w-3.5 h-3.5" /> Ver todos ({total})</>
      )}
    </button>
  );
}

function MatrixView({ summary }) {
  const skills = summary.skill_progress || [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Table2 className="w-5 h-5 text-indigo-600" />
          Matriz de Progreso
        </CardTitle>
        <CardDescription>
          XP de cada estudiante por habilidad — {summary.total_students} estudiantes
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 sm:p-2 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="progress-matrix-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              <th className="text-left py-2.5 px-3 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10">Estudiante</th>
              {skills.map(s => (
                <th key={s.objective_id} className="py-2.5 px-2 text-center font-semibold whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: s.objective_color }} />
                    <span style={{ color: s.objective_color }}>{s.objective_name}</span>
                  </span>
                  {s.max_xp && <span className="block text-[10px] font-normal text-slate-400">max {s.max_xp} XP</span>}
                </th>
              ))}
              <th className="py-2.5 px-2 text-center font-semibold text-slate-600 whitespace-nowrap">Total XP</th>
              <th className="py-2.5 px-2 text-center font-semibold text-slate-600">Nivel</th>
              <th className="py-2.5 px-2 text-center font-semibold text-slate-600">Logros</th>
            </tr>
          </thead>
          <tbody>
            {(summary.students_overview || []).map((st, idx) => (
              <tr key={st.student_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`matrix-row-${st.student_id}`}>
                <td className="py-2 px-3 sticky left-0 bg-white z-10">
                  <div className="flex items-center gap-2">
                    <span className="w-4 text-xs text-slate-400 font-semibold">{idx + 1}</span>
                    <ProfilePhoto photoUrl={st.photo_url} name={st.student_name} size={26} />
                    <span className="font-medium text-slate-800 truncate max-w-[140px]">{st.student_name}</span>
                  </div>
                </td>
                {skills.map(s => {
                  const xp = st.xp_by_objective?.[s.objective_id] || 0;
                  const ref = s.max_xp || (s.levels?.length ? Math.max(...s.levels.map(l => l.xp_required)) : null);
                  const pct = ref ? Math.min(Math.round((xp / ref) * 100), 100) : null;
                  return (
                    <td key={s.objective_id} className="py-2 px-2 text-center">
                      <span className={`font-semibold ${xp > 0 ? 'text-slate-800' : 'text-slate-300'}`}>{xp}</span>
                      {pct != null && (
                        <div className="mt-1 mx-auto w-16">
                          <XPBar pct={pct} color={s.objective_color} height="h-1.5" />
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="py-2 px-2 text-center font-bold text-purple-700">{st.total_xp}</td>
                <td className="py-2 px-2 text-center">
                  <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                    Nv.{st.level} {st.level_name}
                  </Badge>
                </td>
                <td className="py-2 px-2 text-center">
                  <span className="inline-flex items-center gap-1 text-amber-600 font-semibold text-xs">
                    <Award className="w-3.5 h-3.5" />
                    {st.achievements_earned}/{summary.total_achievements || 0}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function GamificationProgress({ progressSummary, refreshing, onRefresh }) {
  const [view, setView] = useState('resumen');
  const [expandedSkills, setExpandedSkills] = useState({});
  const [expandedAchs, setExpandedAchs] = useState({});

  if (!progressSummary) {
    return (
      <Card className="border-dashed border-2 border-slate-200">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <TrendingUp className="w-12 h-12 text-slate-300 mb-4" />
          <p className="text-slate-500">Cargando resumen de progreso...</p>
        </CardContent>
      </Card>
    );
  }

  const toggleSkill = (id) => setExpandedSkills(p => ({ ...p, [id]: !p[id] }));
  const toggleAch = (id) => setExpandedAchs(p => ({ ...p, [id]: !p[id] }));
  const totalStudents = progressSummary.total_students || 0;

  return (
    <div className="space-y-6">
      {/* Toolbar: view toggle + refresh */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          <button
            onClick={() => setView('resumen')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              view === 'resumen' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="progress-view-resumen"
          >
            <LayoutList className="w-3.5 h-3.5" /> Resumen
          </button>
          <button
            onClick={() => setView('matriz')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              view === 'matriz' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="progress-view-matriz"
          >
            <Table2 className="w-3.5 h-3.5" /> Matriz
          </button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-progress-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </Button>
      </div>

      {view === 'matriz' ? (
        <MatrixView summary={progressSummary} />
      ) : (
        <>
          {/* Skill Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Progreso por Habilidad
              </CardTitle>
              <CardDescription>
                Avance de todos los estudiantes en cada habilidad ({totalStudents} estudiantes)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {progressSummary.skill_progress.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No hay habilidades definidas</p>
              ) : (
                <div className="space-y-5">
                  {progressSummary.skill_progress.map(skill => {
                    const all = skill.all_students || skill.top_students || [];
                    const isOpen = !!expandedSkills[skill.objective_id];
                    const visible = isOpen ? all : all.slice(0, 5);
                    return (
                      <div
                        key={skill.objective_id}
                        className="p-3 sm:p-4 rounded-xl border border-slate-100 bg-white space-y-2"
                        data-testid={`skill-progress-${skill.objective_id}`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: skill.objective_color }} />
                            <span className="font-semibold text-slate-800">{skill.objective_name}</span>
                            {skill.max_xp && <span className="text-xs text-slate-400">max {skill.max_xp} XP</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 text-xs">
                              Promedio: {skill.avg_xp ?? 0} XP
                            </Badge>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                              <Zap className="w-3 h-3 mr-0.5" />
                              {skill.total_xp_earned} XP total
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          {visible.map((st, idx) => (
                            <SkillStudentRow key={st.student_id} student={st} rank={idx + 1} color={skill.objective_color} />
                          ))}
                        </div>
                        {all.length > 5 && (
                          <ExpandToggle
                            isOpen={isOpen}
                            total={all.length}
                            onClick={() => toggleSkill(skill.objective_id)}
                            testId={`toggle-skill-students-${skill.objective_id}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Achievement Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-600" />
                Progreso de Logros
              </CardTitle>
              <CardDescription>
                Avance de todos los estudiantes hacia cada logro
              </CardDescription>
            </CardHeader>
            <CardContent>
              {progressSummary.achievement_progress.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No hay logros definidos</p>
              ) : (
                <div className="space-y-4">
                  {progressSummary.achievement_progress.map(ach => {
                    const IconComponent = getIcon(ach.icon);
                    const all = ach.all_students || [];
                    const isOpen = !!expandedAchs[ach.achievement_id];
                    const visible = isOpen ? all : all.slice(0, 5);
                    const earnedPct = totalStudents > 0 ? Math.round((ach.earned_count / totalStudents) * 100) : 0;
                    return (
                      <div
                        key={ach.achievement_id}
                        className="p-3 sm:p-4 bg-slate-50/70 rounded-xl border border-slate-100 space-y-3"
                        data-testid={`achievement-progress-${ach.achievement_id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: ach.achievement_color + '20' }}
                            >
                              <IconComponent className="w-5 h-5" style={{ color: ach.achievement_color }} />
                            </div>
                            <span className="font-semibold text-slate-800 truncate">{ach.achievement_name}</span>
                          </div>
                          <Badge className="text-white flex-shrink-0" style={{ backgroundColor: ach.achievement_color }}>
                            {ach.earned_count} obtenidos
                          </Badge>
                        </div>
                        {/* Class-wide summary bar */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <XPBar pct={earnedPct} color={ach.achievement_color} height="h-2.5" />
                          </div>
                          <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">
                            {ach.earned_count}/{totalStudents} · {earnedPct}%
                          </span>
                        </div>
                        <div className="space-y-0.5">
                          {visible.map(st => (
                            <AchievementStudentRow key={st.student_id} student={st} color={ach.achievement_color} />
                          ))}
                        </div>
                        {all.length > 5 && (
                          <ExpandToggle
                            isOpen={isOpen}
                            total={all.length}
                            onClick={() => toggleAch(ach.achievement_id)}
                            testId={`toggle-ach-students-${ach.achievement_id}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Level Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                Distribución por Nivel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {progressSummary.level_distribution.map(lvl => (
                  <div
                    key={lvl.level}
                    className="flex-1 min-w-[100px] bg-gradient-to-b from-purple-100 to-purple-50 rounded-lg p-3 text-center"
                  >
                    <p className="text-2xl font-bold text-purple-700">{lvl.count}</p>
                    <p className="text-xs text-purple-600">Nivel {lvl.level}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
